import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SubscribeAssetComponent } from '@encomply/feature/manageapps/components/subscribe-asset/subscribe-asset.component';
import { AppIntegrationRecord } from '../../models/integrations/appintegrationrecord.class';
import { AppNewIntegrationRecordPayload } from '../../models/integrations/appnewintegrationpayload.class';
import { AssetService } from '../../models/assetservice.class';
import { Chain } from '../../models/chain.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { IntegrationModal } from '../../interface/integrationmodal.interface';
import { IntegrationsConfigService } from '../../services/integrations-config/integrations-config.service';
import { IntegrationsUtilsService } from '../../services/integrations-utils/integrations-utils.service';
import { UtilsService } from '../../services/utils/utils.service';
import { translate } from '@ngneat/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, Subject, takeUntil } from 'rxjs';

import {
    CloneAndSubscribeConnector,
    GetIntegrationsConfig,
    RedirectToAuthorizationURI,
    SetConnectorId,
} from '../../store/actions/integrations.actions';
import { GetActiveTransformationsByType, GetIntegrationsChains } from '../../store/actions/transformations.actions';
import { GetAllApps, SetManageAppsSelectedServiceIds } from '../../store/actions/manage-apps.actions';
import { GetOrganizationNamesByIds } from '../../store/actions/locations.actions';
import { getAllApps$ } from '../../store/selectors/manage-apps.selector';
import { getAllTransformationsServicesMap$ } from '../../store/selectors/transformations.selector';
import { getIntegrationsSupportedTypes$ } from '../../store/selectors/integrations.selector';
import { getOrgIdVsNameMap$ } from '../../store/selectors/locations.selector';
import { TransformationsState } from '../../store/states/transformations.state';

@Component({
    selector: 'app-view-or-clone-connector',
    templateUrl: './view-or-clone-connector.component.html',
    styleUrls: ['./view-or-clone-connector.component.scss'],
    standalone: false,
})
export class ViewOrCloneConnectorComponent implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    public connector: any;
    public action: string;
    public clonedResourceName = '';
    public subTenants: any;
    public masterTables: any;
    public requiredFields: { [key: string]: string } = {};

    public title = 'Clone & Subscribe Connector';
    public config: IntegrationModal = CommonUtilsService.cloneObject(this._config.connectors);
    public record: AppIntegrationRecord = new AppIntegrationRecord({} as any, this.config);
    public sources: { id: string; displayName: string; value?: string | boolean; pushValue?: string }[] = [];
    private allAppsChains: TransformationsState['organizationsMap']['111']['services'];
    private orgIdVsNames: { [property: string]: string };
    private allApps: AssetService[];

    constructor(
        private store$: Store,
        private actions$: Actions,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<ViewOrCloneConnectorComponent>,
        private _config: IntegrationsConfigService,
        public _integrationUtils: IntegrationsUtilsService,
        private _libUtils: UtilsService,
        private _commonUtils: CommonUtilsService
    ) {}

    closeDialog() {
        this.dialogRef.close();
    }

    public cloneConnector = () => {
        if (this.validateRecord()) {
            this.store$.dispatch(SetManageAppsSelectedServiceIds({ ids: [this.clonedResourceName] }));
            const dialogRef = this.dialog.open(SubscribeAssetComponent, {
                panelClass: ['subscribeAssetModal'],
                disableClose: true,
            });
            if (dialogRef) {
                document.querySelector('.subscribeAssetModal').parentElement.classList.add('parentSubscribeAssetModal');
                dialogRef.componentInstance.isConnector = true;
                dialogRef.componentInstance.submitClone = (selectedChildOrgIds: string[]) => {
                    this.submitConfiguration(selectedChildOrgIds);
                };
            }
        }
    };

    private validateRecord = (record?: AppIntegrationRecord): boolean => {
        const result = this._integrationUtils.isFormValid((record as any) || this.record);
        if (!result) {
            this._libUtils.alertError(translate('Fix issues to proceed'));
        }
        return result;
    };

    public submitConfiguration = (orgIds: string[]) => {
        const record = CommonUtilsService.cloneObject(this.record);
        const config = CommonUtilsService.cloneObject(this.config);
        const data = new AppNewIntegrationRecordPayload(record, config, undefined, undefined, undefined);
        delete data.serviceId; // here backend team will create new service id
        this.store$.dispatch(CloneAndSubscribeConnector({ orgIds, connector: data as any, requiredFields: this.requiredFields || {} }));
    };

    public appChanged = (restApiName: string) => {
        const app = this.allApps?.find(app => app?.restApiName === restApiName);
        if (!app || !app.restApiName) {
            return;
        }
        this.store$.dispatch(
            GetActiveTransformationsByType({
                noAlerts: true,
                serviceId: app?.serviceId,
                restApiName: restApiName,
                transformationType: 'INBOUND',
                forceFetch: false,
            })
        );
        this.store$.dispatch(
            GetIntegrationsChains({
                serviceId: app?.serviceId,
                restApiName: restApiName,
                assetId: app?.assetMetaUId,
                assetName: app?.serviceName,
            })
        );
        this.checkChains();
    };

    checkChains = () => {
        const notFoundOrgs = [];
        this.record?.recordData?.subSections
            ?.find(section => section.id === 'connectorApis')
            ?.records?.forEach(record => {
                const subsection = record?.childSubSections?.find(s => s.displayName === 'Target');
                const restApiName = subsection?.fields.find(f => f.id === 'assetResourceName')?.value;
                const app = this.allApps?.find(app => app?.restApiName === restApiName);
                const transformations = CommonUtilsService.cloneObject(this.allAppsChains?.[app?.restApiName]?.transformations);
                const chains: Chain[] = transformations?.integrationsChains?.inbound || [];
                const activeTransformations = transformations?.activeTransformations?.inbound || [];
                const chainsSorted = chains
                    .sort((a, b) => {
                        if (activeTransformations?.includes(a.chainName) && activeTransformations?.includes(b.chainName)) {
                            return activeTransformations?.indexOf(a.chainName) - activeTransformations?.indexOf(b.chainName);
                        } else if (activeTransformations?.includes(a.chainName)) {
                            return -1;
                        } else if (activeTransformations?.includes(b.chainName)) {
                            return 1;
                        }
                        return 0;
                    })
                    .filter(chain => activeTransformations?.length === 0 || activeTransformations?.includes(chain.chainName));
                const orgIdVsNames = this.orgIdVsNames;
                const providerOrgId = this._commonUtils.getFromStorage('currentOrganization')?.providerOrganizationId;
                const chainslist = [];
                for (const key in orgIdVsNames) {
                    const rawOrgName = orgIdVsNames[key];
                    chainslist.push({
                        orgName: providerOrgId && key === providerOrgId ? 'Default' : rawOrgName,
                        repositoryId: key,
                        chains: [],
                    });
                }
                chainsSorted?.forEach(chain => {
                    let found = false;
                    chainslist.forEach(org => {
                        if (org.repositoryId === chain.repositoryId) {
                            org.chains.push(chain);
                            found = true;
                        }
                    });
                    if (!found) {
                        notFoundOrgs.push(chain.repositoryId);
                    }
                });
                const chainField = subsection?.fields.find(f => f.type === 'CONNECTORS_CHAIN_DROPDOWN');
                if (chainField) {
                    chainField.options = chainslist;
                }
            });
        notFoundOrgs?.length > 0 && this.store$.dispatch(GetOrganizationNamesByIds({ orgIds: notFoundOrgs, noAlerts: true }));
    };

    private initiateSubscriptions() {
        this.store$
            .select(getAllTransformationsServicesMap$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(async data => {
                this.allAppsChains = CommonUtilsService.cloneObject(data);
                this.checkChains();
            });
        this.store$
            .select(getOrgIdVsNameMap$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this.orgIdVsNames = data;
                this.checkChains();
            });

        this.store$
            .select(getAllApps$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(data => {
                this.allApps = CommonUtilsService.cloneObject(data)?.filter(app => app.assetType === 'ASSET');
                const configAppSelection = this.config?.subSections
                    ?.find(s => s.id === 'connectorApis')
                    ?.childSubSections?.find(s => s.displayName === 'Target')
                    ?.fields?.find(configField => configField.id === 'assetResourceName');
                if (configAppSelection) {
                    configAppSelection.options = this.allApps?.map(app => ({
                        id: app.restApiName as string,
                        displayName: app.displayName,
                    }));
                }
                this.record?.recordData?.subSections
                    ?.find(s => s.id === 'connectorApis')
                    ?.records?.forEach(record => {
                        const recordappSelection = record?.childSubSections
                            ?.find(s => s.displayName === 'Target')
                            ?.fields?.find(configField => configField.id === 'assetResourceName');
                        if (recordappSelection) {
                            this.appChanged(recordappSelection.value);
                            recordappSelection.options = this.allApps?.map(app => ({
                                id: app.restApiName as string,
                                displayName: app.displayName,
                            }));
                        }
                    });
                this.checkChains();
            });

        this.store$
            .select(getIntegrationsSupportedTypes$)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(supportedTypes => {
                if (supportedTypes) {
                    this._integrationUtils.updateSupportedTypes(this.config, supportedTypes);
                    this.record?.recordData && this._integrationUtils.updateSupportedTypes(this.record.recordData, supportedTypes);
                }
            });

        this.actions$.pipe(ofType(RedirectToAuthorizationURI), takeUntil(this.unsubscribe)).subscribe((data: any) => {
            this.dialog.closeAll();
            window.open(data.authorizationURI, '_blank');
            this.store$.dispatch(GetAllApps({ isPackagePage: false, forceFetch: true }));
        });
    }

    ngOnInit() {
        if (this.connector) {
            this.record = new AppIntegrationRecord(this.connector, this.config);
            this.title = this.action === 'VIEW' ? 'View Connector: ' + this.connector?.connectorDisplayName : 'Clone & Subscribe Connector';
        }
        this.initiateSubscriptions();
        this.store$.dispatch(GetAllApps({ isPackagePage: false, forceFetch: true, allApps: true }));
        this.store$.dispatch(GetIntegrationsConfig({}));
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.store$.dispatch(SetConnectorId({ connectorId: undefined }));
    }
}
